import {
  createEntity,
  defaultEntityId,
  ENTITY_PHASE_SENSOR,
  type EntityInstance,
} from '@md/engine';
import { type EntityDef, TICK_RATE } from '@md/shared';
import { ElectromagnetDefSchema, polaritySign } from './schemas.js';

/**
 * Électroaimant fixé au décor (concept §3.2).
 *
 * Il n'applique pas la force lui-même : il publie seulement son état
 * (`flags.active`) et son drapeau `attractsPlayers`, que la règle
 * d'interaction `magnetic-objects` traduit en attraction sur le robot du pôle
 * OPPOSÉ. Piège… ou moyen de transport.
 *
 * Trois régimes, par ordre de priorité : `always`, `linkedTo` (commandé par un
 * bouton ou un levier), sinon un cycle automatique dont la phase est calculée
 * à partir du numéro de tick — donc parfaitement déterministe et rejouable.
 */
export function createElectromagnet(def: EntityDef): EntityInstance {
  const cfg = ElectromagnetDefSchema.parse(def);
  const sign = polaritySign(cfg.polarity);
  const halfPeriod = Math.max(1, Math.round(cfg.cycleSeconds * TICK_RATE));
  const offset = Math.round(cfg.phaseSeconds * TICK_RATE);

  return createEntity({
    id: cfg.id ?? defaultEntityId('electromagnet', cfg.x, cfg.y),
    type: 'electromagnet',
    x: cfg.x,
    y: cfg.y,
    phase: ENTITY_PHASE_SENSOR,
    flags: { active: cfg.always ? 1 : 0, polarity: sign, attractsPlayers: 1 },
    appearance: {
      shape: 'ring',
      tint: sign > 0 ? 0x6d3a3f : 0x2f4d6d,
      activeTint: sign > 0 ? 0xff6b6b : 0x5bd6ff,
    },
    update(self, ctx) {
      if (cfg.always) {
        self.flags.active = 1;
        return;
      }
      if (cfg.linkedTo !== undefined) {
        self.flags.active = ctx.signal(cfg.linkedTo) ? 1 : 0;
        return;
      }
      const phase = Math.floor((ctx.tick + offset) / halfPeriod) % 2;
      self.flags.active = phase === 0 ? 1 : 0;
    },
  });
}
